/**
 * First-login Terms & Conditions gate. Wraps the app shell: once the user is
 * authenticated, we check their server-side acceptance (GET /me). If they
 * haven't accepted, we block the whole app on a one-time acceptance screen
 * until they do (POST /me/accept-terms) — or sign out. Acceptance is recorded
 * per user (users.terms_accepted_at), so it's asked exactly once.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Spinner,
  SpaceBetween,
} from "@cloudscape-design/components";
import { acceptTerms, getMe } from "@/core/api/account";
import { useAuth } from "@/core/auth";
import { TERMS_VERSION, TermsBody } from "./TermsContent";

type Status = "loading" | "needed" | "ok" | "error";

export function TermsGate({ children }: { children: ReactNode }) {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<Status>("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      const me = await getMe();
      setStatus(me.terms_accepted_at ? "ok" : "needed");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      await acceptTerms();
      setStatus("ok");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (status === "ok") return <>{children}</>;

  if (status === "loading") {
    return (
      <Box padding="xxl" textAlign="center">
        <Spinner size="large" /> Loading…
      </Box>
    );
  }

  // needed | error → blocking screen on the branded sign-in backdrop.
  return (
    <div className="sigon-login">
      <div className="sigon-login-inner" style={{ width: 760 }}>
        <Container
          header={
            <Header
              variant="h2"
              description={`Please review and accept to continue · Draft ${TERMS_VERSION}`}
            >
              SOC1.lab — Terms &amp; Conditions
            </Header>
          }
        >
          <SpaceBetween size="m">
            {error && (
              <Alert type="error" header="Couldn't continue">
                {error}
              </Alert>
            )}
            {status === "error" ? (
              <SpaceBetween size="m">
                <Box color="text-body-secondary">
                  We couldn't check your acceptance status. Please retry.
                </Box>
                <Button onClick={() => void check()}>Retry</Button>
              </SpaceBetween>
            ) : (
              <>
                <div style={{ maxHeight: "52vh", overflowY: "auto", paddingRight: 8 }}>
                  <TermsBody />
                </div>
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="primary"
                    loading={busy}
                    disabled={busy}
                    onClick={() => void accept()}
                  >
                    I accept
                  </Button>
                  <Button variant="link" disabled={busy} onClick={() => void signOut()}>
                    Sign out
                  </Button>
                </SpaceBetween>
                <Box variant="small" color="text-body-secondary">
                  By selecting "I accept" you agree to these Terms on behalf of your organization.
                </Box>
              </>
            )}
          </SpaceBetween>
        </Container>
      </div>
    </div>
  );
}
